import React, { useEffect, useState } from 'react';
import { Star, Send, LogIn, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { http, callOrMock, USE_LIVE_API } from '../services/http';

const LS_KEY = (id) => `vrs_reviews_${id}`;

/**
 * Product reviews with an "add your review" form. Logged-in users can post;
 * others see a "Log in to review" nudge. Works against the live Laravel API
 * (`GET/POST /api/products/{slug}/reviews`) when USE_LIVE_API=true, else uses
 * localStorage so the preview still lets you post one.
 */
export default function ProductReviews({ product }) {
  const { user, isAuthed } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]   = useState({ rating: 5, title: '', body: '' });
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState('');
  const [err, setErr]     = useState('');

  const slug = product.slug || product.id;

  const load = async () => {
    setLoading(true);
    try {
      const list = await callOrMock(
        async () => {
          const res = await http.get(`/products/${slug}/reviews`);
          return Array.isArray(res) ? res : (res.data || []);
        },
        () => JSON.parse(localStorage.getItem(LS_KEY(slug)) || '[]'),
      );
      setReviews(list);
    } catch (e) { setErr(e?.message || 'Could not load reviews'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body || form.body.length < 3) { setErr('Please write a short review.'); return; }
    setErr(''); setMsg(''); setBusy(true);
    try {
      const payload = {
        name:   user?.name || 'Guest',
        rating: Number(form.rating),
        title:  form.title,
        body:   form.body,
      };
      const created = await callOrMock(
        async () => http.post(`/products/${slug}/reviews`, payload),
        async () => {
          const list = JSON.parse(localStorage.getItem(LS_KEY(slug)) || '[]');
          const rec = { ...payload, id: Date.now(), user_id: user?.id || null, created_at: new Date().toISOString() };
          list.unshift(rec);
          localStorage.setItem(LS_KEY(slug), JSON.stringify(list));
          return rec;
        },
      );
      setReviews([created, ...reviews]);
      setForm({ rating: 5, title: '', body: '' });
      setMsg('Thanks for your review!');
    } catch (e) { setErr(e?.message || 'Could not post your review'); }
    finally { setBusy(false); }
  };

  const total = reviews.length || product.reviews || 0;
 

  const avg = reviews.length
  ? (reviews.reduce((s, r) => s + Number(r.rating || 0), 0) / reviews.length)
  : Number(product.rating || 5);


  const dist  = [5, 4, 3, 2, 1].map(n => ({
    n,
    count: reviews.filter(r => Number(r.rating) === n).length,
  }));

  return (
    <div className="grid lg:grid-cols-3 gap-8 mt-8">
      {/* Summary + submit form */}
      <div className="lg:col-span-1 space-y-4">
        <div className="border border-neutral-200 rounded-xl p-5 sm:p-6 text-center bg-white">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#1e3a2b] grid place-items-center">
            <Star className="w-8 h-8 fill-white text-white" />
          </div>
          <div className="mt-3 text-3xl font-bold text-neutral-900">{avg.toFixed(1)}</div>
          <div className="flex justify-center gap-0.5 mt-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${i < Math.round(avg) ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-300'}`} />
            ))}
          </div>
          <div className="text-xs text-neutral-500 mt-1">{total.toLocaleString('en-IN')} review{total !== 1 && 's'}</div>

          <div className="mt-4 space-y-1.5 text-xs">
            {dist.map(({ n, count }) => {
              const pct = reviews.length ? (count / reviews.length) * 100 : (n === 5 ? 100 : 0);
              return (
                <div key={n} className="flex items-center gap-2">
                  <span className="w-6 flex items-center gap-0.5 text-neutral-700">{n}<Star className="w-3 h-3 fill-yellow-400 text-yellow-400" /></span>
                  <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div className="h-full bg-yellow-400" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-6 text-right text-neutral-600">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-neutral-200 rounded-xl p-5 sm:p-6 bg-white">
          <h3 className="font-semibold text-neutral-900">Share your experience</h3>
          {!isAuthed ? (
            <div className="mt-3 text-sm text-neutral-600">
              <Link to="/login" state={{ next: window?.location?.pathname }} className="inline-flex items-center gap-1.5 text-[#1e3a2b] font-semibold hover:underline">
                <LogIn className="w-4 h-4" /> Log in to write a review
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-3 space-y-3">
              <div>
                <span className="text-xs text-neutral-600">Your rating</span>
                <div className="flex gap-1 mt-1" role="radiogroup" aria-label="Rating">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, rating: n }))}
                      data-testid={`review-star-${n}`}
                      className="p-1"
                      aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    >
                      <Star className={`w-6 h-6 transition-colors ${n <= form.rating ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-300 hover:text-yellow-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Title (optional)"
                data-testid="review-title"
                maxLength={120}
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1e3a2b]"
              />
              <textarea
                required
                rows={4}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="What did you love (or not love)?"
                data-testid="review-body"
                maxLength={2000}
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1e3a2b]"
              />
              {err && <div className="text-xs text-red-600">{err}</div>}
              {msg && <div className="text-xs text-emerald-700 flex items-center gap-1"><Check className="w-3 h-3" /> {msg}</div>}
              <button
                disabled={busy}
                data-testid="review-submit"
                className="w-full bg-[#1e3a2b] hover:bg-[#2a5040] disabled:opacity-60 text-white py-2.5 rounded-md font-semibold flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" /> {busy ? 'Posting…' : 'Post review'}
              </button>
              {!USE_LIVE_API && <div className="text-[10px] text-neutral-400 text-center">Preview mode — reviews saved to this browser only.</div>}
            </form>
          )}
        </div>
      </div>

      {/* List */}
      <div className="lg:col-span-2">
        {loading ? (
          <div className="text-neutral-500 py-6">Loading reviews…</div>
        ) : reviews.length === 0 ? (
          <div className="border border-dashed border-neutral-300 rounded-xl p-10 text-center text-neutral-500">
            <Star className="w-10 h-10 mx-auto text-neutral-300" />
            <p className="mt-3 font-medium text-neutral-700">No reviews yet — be the first!</p>
          </div>
        ) : (
          <div className="space-y-5">
            {reviews.map((r) => {
              const initial = (r.name || 'A').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
              const dt = r.created_at ? new Date(r.created_at) : new Date();
              return (
                <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-neutral-200 pb-5">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full grid place-items-center text-sm font-semibold bg-neutral-100 text-neutral-700">{initial}</div>
                      <div>
                        <div className="font-semibold text-neutral-900">{r.name}</div>
                        <div className="flex gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }).map((_, k) => (
                            <Star key={k} className={`w-3.5 h-3.5 ${k < Number(r.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-300'}`} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-neutral-500">{dt.toLocaleDateString('en-IN')}</div>
                  </div>
                  {r.title && <div className="mt-3 font-semibold text-neutral-900">{r.title}</div>}
                  {r.body && <p className="mt-1 text-neutral-700 leading-relaxed whitespace-pre-line">{r.body}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
